<template>
  <div class="editpage">
    <image-selector
      v-if="model"
      v-model="image"
      class="view"
      :ratio="2.0 / 5.0"
      :defaultImage="imagePath"
      placeholder="/img/placeholders/worldview.png"
      @input="change"
    />
    <world-view-form v-if="model" :novel-id="novelId" v-model="model" />
    <button-default class="submit" :disabled="!model || !model.name || saving" @click="submit">{{
      $t("worldViewEditPage.updateButton")
    }}</button-default>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import WorldViewForm from "@/components/organisms/WorldViewForm.vue";
import ButtonDefault from "@/components/atoms/ButtonDefault.vue";
import ImageSelector from "@/components/molecules/ImageSelector.vue";
import { submitImage } from "@/lib/image-save";
import { cloneDeep } from "lodash";
import { UpdateWorldView, WorldView } from "@/lib/models";
import { handleShortcutKeys, TriggerKey } from "@/lib/utils/keyboardShortcuts";

export default Vue.extend<Data, Methods, Computed, Props>({
  // NOTE: metaタグの設定
  metaInfo: {
    meta: [
      {
        name: "robots",
        content: "none",
      },
    ],
  },
  components: { WorldViewForm, ButtonDefault, ImageSelector },
  props: {
    novelId: String,
    worldViewId: String,
  },
  data() {
    return {
      model: null,
      image: null,
      saving: false,
      unbindShortcutKeys: null,
    };
  },
  async created() {
    const { novelId, worldViewId } = this;
    await this.$store.dispatch("worldViewModule/initialize", novelId);
    const { worldView } = this;
    if (!worldView) {
      this.$router.replace({
        name: "worldViews",
        params: worldViewId ? { novelId, worldViewId } : { novelId },
      });
      return;
    }
    const { name, detail, additionalColumn, image } = worldView;
    this.image = image;
    this.model = { name, detail, additionalColumn };

    // 保存ボタンのショートカットキーの有効化と解除関数の取得
    this.unbindShortcutKeys = handleShortcutKeys([
      { trigger: TriggerKey.Meta, keys: ["s"], callback: this.submit },
      { trigger: TriggerKey.Ctrl, keys: ["s"], callback: this.submit },
    ]);
  },
  beforeDestroy() {
    // コンポーネントが破棄される前に保存ボタンのショートカットキーを解除
    if (this.unbindShortcutKeys) this.unbindShortcutKeys();
  },
  computed: {
    worldView() {
      return cloneDeep(this.$store.getters["worldViewModule/worldView"](this.worldViewId));
    },
    imagePath() {
      const { novelId, worldViewId, image } = this;
      if (!image) return null;
      if (image.startsWith("file:")) {
        return `file:novels/${novelId}/worldviews/${worldViewId}.jpg`;
      }
      if (image.startsWith("preset:")) {
        return image;
      }
      return null;
    },
  },
  methods: {
    change() {
      this.setModified(true);
    },
    async submit() {
      if (!this.model || !this.model.name || this.saving) {
        return;
      }
      try {
        this.saving = true;
        const { model } = this;
        const { novelId, worldViewId, image } = this;
        const { additionalColumn } = model;
        model.additionalColumn = additionalColumn.filter((item) => item.label || item.content);

        const updateWorldViewInput: UpdateWorldView = {
          novelId,
          worldViewId,
          image,
          ...model,
        };
        await this.$store.dispatch("worldViewModule/updateWorldView", updateWorldViewInput);
        await submitImage(`novels/${novelId}/worldviews/${worldViewId}.jpg`);

        this.$notify({
          title: this.$t("common.saved"),
          type: "success",
        });

        this.setModified(false);

        this.$router.push({
          name: "worldViews",
          params: { novelId, worldViewId },
        });
      } finally {
        this.saving = false;
      }
    },
  },
});

interface Props {
  novelId: string;
  worldViewId: string;
}

interface Data {
  model: {
    name: string;
    detail: any[];
    additionalColumn: any[];
  } | null;
  image: string | null;
  saving: boolean;
  unbindShortcutKeys: (() => void) | null;
}

interface Computed {
  worldView: WorldView;
  imagePath: string | null;
}

interface Methods {
  change(): void;
  submit(): Promise<void>;
}
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.editpage {
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
  padding: 0 12.5%;
  padding-bottom: 60px;
  @include mq(sp) {
    padding: 0;
    padding-bottom: 50px;
  }
}
.content-main {
  margin: $margin-m;
}
.button {
  margin: 15px auto;
  padding: 30px auto;
  border-radius: var(--radius-rounded);
  font-weight: var(--font-weight-bold);
  width: 350px;

  @include mq(sp) {
    margin: 15px 30px;
    padding: 15px 30px;
    width: auto;
  }
}
.submit {
  position: relative;
  color: var(--color-text-on-fixed-bg);
  background-color: var(--color-primary);
  border-color: var(--color-primary);

  @include hover-overlay;

  &:disabled {
    background-color: var(--color-surface-muted);
    color: var(--color-inactive);
    border: solid 1px var(--color-border);
    cursor: not-allowed;
    &::after {
      display: none;
    }
  }
}
</style>
